import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import BizInput from '../../../Core/Components/Mini/BizInput';
import BizTextArea from '../../../Core/Components/Mini/BizTextArea';
import { BizAutoComplete } from '../../../Core/Components/Mini/BizAutoComplete';
import { BizSelect } from '../../../Core/Components/Mini/BizSelect';
import { useMediabankGetFilesForUser, useMediabankMutations } from '../../../Core/Hooks/useMediabank';
import { ObjectType } from '../../../Core/Types/base';
import { MediabankFileTypes, MediabankUploadLocation } from '../../../Core/Types/mediabank';
import {
	useGetExpenseProjectOptions,
	useCreateTravelReport,
	useUpdateTravelReport,
	useGetTravelReportTypes,
} from '../../../Core/Hooks/useTravelReport';
import { TravelReport } from '../../../Core/Types/admin/timereport';
import { CreateTravelReportRequest } from '../../../Core/Types/timereport';
import { useTimereportSettings } from '../../../Core/Hooks/admin/useTimereportSettings';
import { StatusState } from '../../../Core/Hooks/useBizQuery';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import { getPathWithRootPath } from '../../../Core/Services/common';
import './ExpenseForm.scss';

type ExpenseFormProps = {
	existingReport: TravelReport | null;
	onClose: () => void;
};

const ExpenseForm = ({ existingReport, onClose }: ExpenseFormProps) => {
	const { t } = useTranslation();
	const { createAsync } = useCreateTravelReport();
	const { updateAsync } = useUpdateTravelReport();
	const { uploadFileAsync, uploadFileResult, unlinkFileFromObjectAsync } = useMediabankMutations();
	const {
		settings,
		isLoading: settingsLoading,
		loadStatusState: settingsStatus,
		refetch: reloadSettings,
	} = useTimereportSettings();
	const { projectOptions, isLoading: projectsLoading } = useGetExpenseProjectOptions();
	const { travelReportTypes } = useGetTravelReportTypes();
	const [currentReport, setCurrentReport] = useState<TravelReport | null>(existingReport);
	const [receiptError, setReceiptError] = useState('');
	const [saveError, setSaveError] = useState('');
	const [saveStatus, setSaveStatus] = useState<StatusState>('INITIAL');
	const [pendingReceipt, setPendingReceipt] = useState<File | null>(null);
	const receiptInputRef = useRef<HTMLInputElement | null>(null);
	const pendingReplaceReceiptIdsRef = useRef<number[]>([]);
	const savingRef = useRef(false);

	const isEditing = currentReport !== null;

	type ProjectOption = { Id: number; Name: string };

	const typeOptions = useMemo(() => {
		if (!travelReportTypes) return [];
		return travelReportTypes.filter((t) => t.systemName !== 'Drivingbycar').map((t) => ({ Id: t.id, Name: t.name }));
	}, [travelReportTypes]);

	const [form, setForm] = useState({
		travelReportTypeId: currentReport?.travelReportTypeId ?? 0,
		projectId: currentReport?.projectId ?? 0,
		activityId: currentReport?.activityId ?? 0,
		date: currentReport?.date.split('T')[0] ?? formatLocalDate(new Date()),
		dateTo: '',
		from: '',
		to: '',
		distance: 0,
		amount: currentReport?.price ?? 0,
		vat: currentReport?.vat ?? 0,
		comment: currentReport?.comment ?? '',
	});

	useEffect(() => {
		if (!isEditing && projectOptions.length === 1) {
			setForm((prev) => (prev.projectId ? prev : { ...prev, projectId: projectOptions[0].Id }));
		}
	}, [isEditing, projectOptions]);
	const hasSelectedProject = projectOptions.some((project) => project.Id === form.projectId);

	const receiptFiles = useMediabankGetFilesForUser(
		currentReport
			? {
					ObjectIds: [currentReport.id],
					ObjectTypes: [ObjectType.TimeReportExpense],
					FileTypes: [MediabankFileTypes.Image.id, MediabankFileTypes.Document.id],
					FillSourceUrls: true,
				}
			: undefined
	);

	const handleProjectChange = (opt: ProjectOption | null) => {
		if (isLocked || savingRef.current) return;
		setForm((prev) => ({ ...prev, projectId: opt?.Id ?? 0 }));
	};

	const handleTypeChange = (opt: { Id: number; Name: string } | null) => {
		if (isLocked || savingRef.current) return;
		setForm((prev) => ({ ...prev, travelReportTypeId: opt?.Id ?? 0 }));
	};

	const handleUploadReceiptClick = () => {
		if (isLocked || savingRef.current || receiptsLoading) return;
		receiptInputRef.current?.click();
	};

	const handleReceiptSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file || isLocked || savingRef.current || receiptsLoading) {
			return;
		}

		setPendingReceipt(file);
		setReceiptError('');
		setSaveStatus('INITIAL');
	};

	const handleSave = async () => {
		if (isLocked || savingRef.current || settingsLoading || !hasSelectedProject || !form.travelReportTypeId) {
			return;
		}

		savingRef.current = true;
		setSaveStatus('LOADING');
		setSaveError('');
		setReceiptError('');
		let reportSaved = false;
		try {
			const data: CreateTravelReportRequest = {
				...form,
				dateTo: settings.expenseHideDateTo ? '' : form.dateTo,
				vat: settings.expenseHideVat ? 0 : form.vat,
			};
			const savedReport = currentReport ? await updateAsync({ ...data, id: currentReport.id }) : await createAsync(data);
			if (!savedReport?.id) throw new Error('No saved expense returned.');
			// Keep the saved ID even if uploading fails, so retrying cannot create another expense.
			setCurrentReport(savedReport);
			setForm((prev) => ({ ...prev, date: savedReport.date.split('T')[0], dateTo: '', vat: data.vat }));
			reportSaved = true;

			if (pendingReceipt) {
				const uploadedReceipt = await uploadFileAsync(
					pendingReceipt,
					pendingReceipt.name,
					'',
					false,
					false,
					MediabankUploadLocation.TimeReportExpense,
					{ linkToObject: { ObjectId: savedReport.id, ObjectType: ObjectType.TimeReportExpense } }
				);
				if (!uploadedReceipt?.Id) throw new Error('No uploaded receipt returned.');
				pendingReplaceReceiptIdsRef.current = Array.from(
					new Set([
						...pendingReplaceReceiptIdsRef.current,
						...(receiptFiles ?? []).map((file) => file.Id),
						...(currentReceipt ? [currentReceipt.Id] : []),
					])
				).filter((id) => id !== uploadedReceipt.Id);
				setPendingReceipt(null);
			}

			// Only remove previous receipts after the replacement was uploaded successfully.
			// Retain failed unlink IDs so Save can retry cleanup without uploading again.
			while (pendingReplaceReceiptIdsRef.current.length > 0) {
				await unlinkFileFromObjectAsync(
					pendingReplaceReceiptIdsRef.current[0],
					ObjectType.TimeReportExpense,
					savedReport.id
				);
				pendingReplaceReceiptIdsRef.current.shift();
			}
			setSaveStatus('SUCCESS');
		} catch {
			if (reportSaved) {
				setReceiptError(t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.RECEIPT_SAVE_FAILED'));
			} else {
				setSaveError(t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.SAVE_FAILED'));
			}
			setSaveStatus('ERROR');
		} finally {
			savingRef.current = false;
		}
	};

	const updateField = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
		if (isLocked || savingRef.current) return;
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const isLocked = currentReport?.isSubmitted === true || currentReport?.isAttested === true;
	const isSaving = saveStatus === 'LOADING';
	const receiptsLoading = currentReport !== null && receiptFiles === undefined;
	const currentReceipt = uploadFileResult ?? receiptFiles?.[0];
	const hideProject = settings.expenseHideSingleProject && projectOptions.length === 1 && hasSelectedProject;

	if (settingsLoading && settingsStatus === 'ERROR') {
		return (
			<div className='biz-expense-form'>
				<p role='alert'>{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.SETTINGS_LOAD_FAILED')}</p>
				<BizButton
					text={t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.RETRY')}
					onClick={() => reloadSettings()}
					legacy={false}
				/>
			</div>
		);
	}
	if (projectsLoading || settingsLoading) return <p>{t('TIMEREPORT_USER.EXPENSE.FORMS.STATE.LOADING')}</p>;

	return (
		<div className='biz-expense-form'>
			<input
				ref={receiptInputRef}
				type='file'
				accept='.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff,.heic,.heif'
				onChange={handleReceiptSelected}
				disabled={isLocked || isSaving || receiptsLoading}
				hidden
			/>
			<fieldset className='biz-expense-form__fields' disabled={isLocked || isSaving}>
				<div className='biz-expense-form__field'>
					<span className='biz-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.TYPE')}</span>
					<BizSelect
						options={typeOptions}
						selectedId={form.travelReportTypeId || null}
						onChange={handleTypeChange}
						disabled={isLocked || isSaving}
						noSelectText={t('TIMEREPORT_USER.EXPENSE.FORMS.PLACEHOLDERS.SELECT_TYPE')}
					/>
				</div>

				{!hideProject && (
					<div className='biz-expense-form__field'>
						<span className='biz-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.PROJECT')}</span>
						<BizAutoComplete
							hideShrunkLabel
							options={projectOptions}
							value={projectOptions.find((p) => p.Id === form.projectId) ?? undefined}
							onChange={(opt) => handleProjectChange(opt)}
							componentLabel={t('TIMEREPORT_USER.EXPENSE.FORMS.PLACEHOLDERS.SEARCH_PROJECT')}
						/>
					</div>
				)}

				<div className='biz-expense-form__row'>
					<div className='biz-expense-form__field'>
						<span className='biz-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.DATE')}</span>
						<BizInput
							type='date'
							value={form.date}
							onChange={(e) => updateField('date', e.target.value)}
							readonly={isLocked || isSaving}
						/>
					</div>
					{!isEditing && !settings.expenseHideDateTo && (
						<div className='biz-expense-form__field'>
							<span className='biz-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.DATE_TO')}</span>
							<BizInput
								type='date'
								value={form.dateTo}
								onChange={(e) => updateField('dateTo', e.target.value)}
								readonly={isLocked || isSaving}
								min={form.date}
							/>
						</div>
					)}
				</div>

				<div className='biz-expense-form__row'>
					<div className='biz-expense-form__field'>
						<span className='biz-expense-form__label'>
							{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.TOTAL_AMOUNT_PER_DAY')}
						</span>
						<BizInput
							type='number'
							value={String(form.amount)}
							onChange={(e) => updateField('amount', parseFloat(e.target.value) || 0)}
							readonly={isLocked || isSaving}
							min='0'
						/>
					</div>
					{!settings.expenseHideVat && (
						<div className='biz-expense-form__field'>
							<span className='biz-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.VAT_PART')}</span>
							<BizInput
								type='number'
								value={String(form.vat)}
								onChange={(e) => updateField('vat', parseFloat(e.target.value) || 0)}
								readonly={isLocked || isSaving}
								min='0'
							/>
						</div>
					)}
				</div>

				<div className='biz-expense-form__field'>
					<span className='biz-expense-form__label'>
						{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.COMMENT', {
							defaultValue: t('TIMEREPORT_USER.EXPENSE.FORMS.SHARED.COMMENT'),
						})}
					</span>
					<BizTextArea
						value={form.comment}
						onChange={(e) => updateField('comment', e.target.value)}
						readonly={isLocked || isSaving}
					/>
				</div>

				<div className='biz-expense-form__field'>
					<span className='biz-expense-form__label'>{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.RECEIPT')}</span>
					<div className='biz-expense-form__receipt-row'>
						<div className='biz-expense-form__receipt-meta'>
							{pendingReceipt ? (
								<span>{pendingReceipt.name}</span>
							) : currentReceipt ? (
								<a
									href={getPathWithRootPath(currentReceipt.FileUrl)}
									target='_blank'
									rel='noreferrer'
									className='biz-expense-form__receipt-link'
								>
									{currentReceipt.Name}
								</a>
							) : (
								<span className='biz-expense-form__help-text'>
									{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.NO_RECEIPT')}
								</span>
							)}
						</div>
						<BizButton
							text={t(
								currentReceipt || pendingReceipt
									? 'TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.REPLACE_RECEIPT'
									: 'TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.SELECT_RECEIPT'
							)}
							onClick={handleUploadReceiptClick}
							disabled={isLocked || isSaving || receiptsLoading}
							legacy={false}
						/>
						{pendingReceipt && (
							<BizButton
								text={t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.CLEAR_RECEIPT')}
								onClick={() => {
									setPendingReceipt(null);
									setReceiptError('');
								}}
								disabled={isLocked || isSaving}
								legacy={false}
							/>
						)}
					</div>
					{pendingReceipt && (
						<p className='biz-expense-form__help-text'>
							{t('TIMEREPORT_USER.EXPENSE.FORMS.EXPENSE.RECEIPT_PENDING')}
						</p>
					)}
					{receiptError && (
						<p className='biz-expense-form__help-text' role='alert'>
							{receiptError}
						</p>
					)}
				</div>
			</fieldset>
			{saveError && (
				<p className='biz-expense-form__help-text' role='alert'>
					{saveError}
				</p>
			)}

			<div className='biz-expense-form__actions dialog-btn-actions'>
				<BizButton
					text={t('TIMEREPORT_USER.EXPENSE.FORMS.ACTIONS.CLOSE')}
					onClick={onClose}
					disabled={isSaving}
					legacy={false}
				/>
				<BizButton
					text={
						isEditing
							? t('TIMEREPORT_USER.EXPENSE.FORMS.ACTIONS.UPDATE')
							: t('TIMEREPORT_USER.EXPENSE.FORMS.ACTIONS.CREATE')
					}
					onClick={handleSave}
					state={saveStatus}
					// icon='far fa-save'
					disabled={isLocked || isSaving || !hasSelectedProject || form.travelReportTypeId === 0}
					isLead
					legacy={false}
				/>
			</div>
		</div>
	);
};

export default ExpenseForm;
